import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldAlert, Plus, Loader2, X, Search, Car, User, Calendar, DollarSign,
  MessageSquare, Trash2, RefreshCcw, AlertOctagon,
} from "lucide-react";
import { api } from "../../../lib/api";

const KINDS = [
  { key: "accident",            label: "Accident" },
  { key: "damage",              label: "Vehicle damage" },
  { key: "smoking",             label: "Smoking" },
  { key: "late_return",         label: "Late return" },
  { key: "unauthorized_driver", label: "Unauthorized driver" },
  { key: "towing",              label: "Towing" },
  { key: "impound",             label: "Impound" },
  { key: "traffic_ticket",      label: "Traffic ticket" },
  { key: "toll",                label: "Toll issue" },
  { key: "chargeback",          label: "Chargeback" },
  { key: "payment_dispute",     label: "Payment dispute" },
  { key: "insurance_issue",     label: "Insurance issue" },
  { key: "customer_complaint",  label: "Customer complaint" },
  { key: "vehicle_recovery",    label: "Vehicle recovery" },
  { key: "other",               label: "Other" },
];

const STATUSES = [
  { key: "OPEN",                 label: "Open",                 tone: "red"    },
  { key: "INVESTIGATING",        label: "Investigating",        tone: "amber"  },
  { key: "WAITING_ON_CUSTOMER",  label: "Waiting on customer",  tone: "amber"  },
  { key: "WAITING_ON_INSURANCE", label: "Waiting on insurance", tone: "amber"  },
  { key: "PAYMENT_DUE",          label: "Payment due",          tone: "amber"  },
  { key: "RESOLVED",             label: "Resolved",             tone: "lime"   },
  { key: "CLOSED",               label: "Closed",               tone: "white"  },
];

const TONE = {
  red:   "bg-red-500/15 text-red-200 border-red-400/40",
  amber: "bg-amber-400/15 text-amber-200 border-amber-400/40",
  lime:  "bg-lime-400/15 text-lime-200 border-lime-400/40",
  white: "bg-white/5 text-white/70 border-white/15",
};

/**
 * CRMIncidentsTab — Incident Center. Fully-featured tracker for accidents,
 * damage, smoking, tolls, chargebacks etc. Powered by /admin/incidents.
 */
export default function CRMIncidentsTab() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ counts_by_status: {}, open_incidents: 0, total_exposure: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/incidents", { params: { status: statusFilter, kind: kindFilter } }),
        api.get("/admin/incidents/stats"),
      ]);
      setRows(r.data || []);
      setStats(s.data || {});
    } catch {
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter, kindFilter]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.customer_name, r.customer_email, r.customer_phone, r.vehicle_name,
       r.description, r.kind, r.booking_id]
        .some((f) => (f || "").toLowerCase().includes(needle))
    );
  }, [rows, q]);

  return (
    <div data-testid="crm-incidents-tab" className="space-y-4">
      {/* Header + stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard label="Open incidents" value={stats.open_incidents || 0} icon={AlertOctagon} tone="red" />
        <StatCard label="Investigating" value={stats.counts_by_status?.INVESTIGATING || 0} tone="amber" />
        <StatCard label="Payment due" value={stats.counts_by_status?.PAYMENT_DUE || 0} tone="amber" />
        <StatCard label="Total exposure" value={`$${(stats.total_exposure || 0).toLocaleString()}`} icon={DollarSign} tone="red" />
      </div>

      {/* Filters + new */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="crm-inc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, vehicle, description…"
            className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 focus:border-red-400/60 rounded-full text-sm text-white outline-none"
          />
        </div>
        <select data-testid="crm-inc-status"
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-black/50 border border-white/15 rounded-full px-3 py-2 text-xs text-white outline-none">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select data-testid="crm-inc-kind"
          value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
          className="bg-black/50 border border-white/15 rounded-full px-3 py-2 text-xs text-white outline-none">
          <option value="all">All types</option>
          {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <button data-testid="crm-inc-refresh" onClick={load}
          className="p-2 rounded-lg border border-white/15 text-white/70 hover:bg-white/5 transition">
          <RefreshCcw size={13} className={loading ? "animate-spin" : ""} />
        </button>
        <button data-testid="crm-inc-new" onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-xs font-bold label-caps transition">
          <Plus size={12} /> New incident
        </button>
      </div>

      {/* List */}
      <div className="rounded-2xl border border-red-400/25 bg-black/25 overflow-hidden">
        <div className="p-3 border-b border-white/5 label-caps text-[10px] text-red-200 tracking-widest">
          {loading ? "loading…" : `${filtered.length} incident${filtered.length === 1 ? "" : "s"}`}
        </div>
        {loading ? (
          <div className="p-10 text-white/50 text-sm inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={14} className="animate-spin text-red-300" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-white/50 text-sm">No incidents match.</div>
        ) : (
          <div className="divide-y divide-white/5 max-h-[65vh] overflow-auto">
            {filtered.map((r) => {
              const st = STATUSES.find((s) => s.key === r.status) || STATUSES[0];
              const k = KINDS.find((x) => x.key === r.kind);
              return (
                <button key={r.id} data-testid={`crm-inc-row-${r.id}`}
                  onClick={() => setSelected(r)}
                  className="w-full text-left p-3 flex items-center gap-3 hover:bg-red-500/[0.04] transition">
                  <div className="w-9 h-9 rounded-full bg-red-500/15 border border-red-400/40 flex items-center justify-center shrink-0">
                    <ShieldAlert size={13} className="text-red-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm font-medium truncate inline-flex items-center gap-2">
                      {k?.label || r.kind}
                      <span className={`label-caps text-[8px] px-1.5 py-0.5 rounded-full border ${TONE[st.tone]}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="text-white/50 text-[11px] truncate mt-0.5">
                      {r.customer_name || "—"} {r.vehicle_name ? `· ${r.vehicle_name}` : ""}
                    </div>
                    <div className="text-white/40 text-[10px] truncate">{r.description}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {r.financial_exposure > 0 && (
                      <div className="text-red-200 font-medium text-sm">${r.financial_exposure.toFixed(0)}</div>
                    )}
                    <div className="text-white/40 text-[10px]">
                      {r.date_occurred ? new Date(r.date_occurred).toLocaleDateString() : ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showNew && <NewIncidentModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {selected && (
        <IncidentDetailModal
          incident={selected}
          onClose={() => setSelected(null)}
          onChange={(fresh) => { setSelected(fresh); load(); }}
          onDelete={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon = ShieldAlert, tone = "red" }) {
  return (
    <div className={`rounded-xl border p-3 ${TONE[tone]}`}>
      <div className="flex items-center justify-between">
        <div className="label-caps text-[9px] tracking-widest opacity-80">{label}</div>
        <Icon size={12} className="opacity-70" />
      </div>
      <div className="font-serif text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function NewIncidentModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    kind: "damage", customer_name: "", customer_email: "", customer_phone: "",
    vehicle_name: "", description: "", financial_exposure: "", booking_id: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Description required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/incidents", {
        ...form,
        financial_exposure: form.financial_exposure ? Number(form.financial_exposure) : 0,
      });
      toast.success("Incident logged");
      onCreated();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} data-testid="crm-inc-new-form"
        className="bg-ink-900 border border-red-400/30 rounded-2xl max-w-lg w-full p-5 space-y-3 my-auto max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps text-red-300 text-[10px]">Report</div>
            <h3 className="font-serif text-xl">New incident</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>
        <Field label="Type">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none">
            {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} />
          <Field label="Phone" value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} />
          <Field label="Email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} type="email" />
          <Field label="Vehicle" value={form.vehicle_name} onChange={(v) => setForm({ ...form, vehicle_name: v })} />
          <Field label="Booking ID (optional)" value={form.booking_id} onChange={(v) => setForm({ ...form, booking_id: v })} />
          <Field label="Financial exposure ($)" value={form.financial_exposure} onChange={(v) => setForm({ ...form, financial_exposure: v })} type="number" />
        </div>
        <Field label="Description" required>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4} required
            className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none resize-none" />
        </Field>
        <button type="submit" disabled={saving} data-testid="crm-inc-new-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-400 text-white font-bold text-xs label-caps tracking-widest disabled:opacity-50">
          {saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Plus size={13} /> Log incident</>}
        </button>
      </form>
    </div>
  );
}

function IncidentDetailModal({ incident, onClose, onChange, onDelete }) {
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const patch = async (body) => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/incidents/${incident.id}`, body);
      onChange(r.data);
      if (body.add_note) setNoteDraft("");
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this incident?")) return;
    await api.delete(`/admin/incidents/${incident.id}`);
    onDelete();
    toast.success("Deleted");
  };
  const st = STATUSES.find((s) => s.key === incident.status) || STATUSES[0];
  const k = KINDS.find((x) => x.key === incident.kind);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-start justify-center overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-900 border border-red-400/30 rounded-2xl max-w-2xl w-full my-8 p-5 space-y-4"
        data-testid="crm-inc-detail" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="label-caps text-red-300 text-[10px]">Incident · {k?.label}</div>
            <h3 className="font-serif text-2xl mt-0.5">{incident.customer_name || "Unknown customer"}</h3>
            <div className="text-white/50 text-xs mt-1">
              {incident.vehicle_name && <>{incident.vehicle_name} · </>}
              {incident.date_occurred && new Date(incident.date_occurred).toLocaleString()}
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={del} className="p-2 rounded-full text-white/40 hover:text-red-300 transition" title="Delete">
              <Trash2 size={14} />
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <div className="label-caps text-[9px] text-white/50 mb-1">Status</div>
            <select value={incident.status} onChange={(e) => patch({ status: e.target.value })} disabled={saving}
              className={`w-full rounded-lg px-3 py-2 text-sm outline-none border ${TONE[st.tone]}`}>
              {STATUSES.map((s) => <option key={s.key} value={s.key} className="bg-ink-900">{s.label}</option>)}
            </select>
          </div>
          <div>
            <div className="label-caps text-[9px] text-white/50 mb-1">Financial exposure ($)</div>
            <input type="number" defaultValue={incident.financial_exposure || 0}
              onBlur={(e) => patch({ financial_exposure: Number(e.target.value || 0) })}
              className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
        </div>

        <div>
          <div className="label-caps text-[9px] text-white/50 mb-1">Description</div>
          <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-white text-sm whitespace-pre-wrap">
            {incident.description || "—"}
          </div>
        </div>

        <div>
          <div className="label-caps text-[9px] text-white/50 mb-2">Activity notes</div>
          <div className="flex gap-2 mb-3">
            <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Add a note (call log, photo received, adjuster update…)"
              className="flex-1 bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
            <button onClick={() => noteDraft.trim() && patch({ add_note: noteDraft.trim() })}
              disabled={!noteDraft.trim() || saving}
              className="px-3 py-2 rounded-lg bg-red-500 text-white text-xs font-bold label-caps inline-flex items-center gap-1 disabled:opacity-40">
              <MessageSquare size={11} /> Add
            </button>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-auto">
            {(incident.notes || []).slice().reverse().map((n, i) => (
              <div key={i} className="text-xs text-white/70 border-l-2 border-red-400/40 pl-2">
                {n.text}
                <div className="text-[10px] text-white/40 mt-0.5">{new Date(n.at).toLocaleString()} · {n.by}</div>
              </div>
            ))}
            {(incident.notes || []).length === 0 && (
              <div className="text-white/40 text-xs italic">No notes yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, children }) {
  return (
    <label className="block">
      <span className="label-caps text-[9px] text-white/60 tracking-widest">{label}</span>
      {children || (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} required={required}
          className="mt-1 w-full bg-black/50 border border-white/15 focus:border-red-400/60 rounded-lg px-3 py-2 text-white text-sm outline-none" />
      )}
    </label>
  );
}
